import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext } from '@hello-pangea/dnd';
import { useAuth } from '../contexts/AuthContext';
import { useProjectAccess } from '../hooks/useProjectAccess';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useProjectData } from '../hooks/useProjectData';
import ProjectHeader from './project/ProjectHeader';
import ProjectBoard from './project/ProjectBoard';
import TodoList from './TodoList';
import CardModal from './CardModal';
import SwitchBoardButton from './SwitchBoardButton';
import SwitchBoardModal from './SwitchBoardModal';
import Loader from './Loader';
import {
    handleTodoOperations,
    handleColumnOperations,
    handleCardOperations,
    updateProject
} from '../utils/projectHandlers';
import { addActivity } from '../utils/cardUtils'; // Import addActivity
import { exportProjectAsJson } from '../utils/projectExportUtils';
import { isToday as dateFnsIsToday, isPast as dateFnsIsPast, parseISO as dateFnsParseISO } from 'date-fns';
import { isDateThisWeek, isDateNextWeek } from '../utils/dateUtils';
import {
    ProjectContainer,
    ContentContainer,
    BackgroundCredit
} from '../styles/ProjectPage.styles';

function ProjectPage() {
    const { id, cardId } = useParams(); // id is projectId
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isLoading: accessLoading, hasAccess } = useProjectAccess(id);
    const {
        project,
        setProject,
        loading,
        error,
        labels,
        background,
        projectMembers
    } = useProjectData(id);

    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [isCollapsed, setIsCollapsed] = useLocalStorage(`project-${id}-header-collapsed`, false);
    const [isTodoCollapsed, setIsTodoCollapsed] = useLocalStorage(`project-${id}-todo-collapsed`, false);
    const [collapsedColumnIds, setCollapsedColumnIds] = useLocalStorage(`project-${id}-collapsed-columns`, []);
    const [areLabelsExpanded, setAreLabelsExpanded] = useLocalStorage(`project-${id}-labels-expanded`, false);
    const [textareaHeight, setTextareaHeight] = useState('auto');
    const [limitColumnHeight, setLimitColumnHeight] = useLocalStorage(`project-${id}-limit-column-height`, true);
    const [enableGithubLinks, setEnableGithubLinks] = useLocalStorage(`project-${id}-enable-github-links`, true); // Default to true
    const [searchTerm, setSearchTerm] = useState('');
    const [isSwitchBoardOpen, setIsSwitchBoardOpen] = useState(false);
    const [filters, setFilters] = useLocalStorage(`project-${id}-card-filters`, {
        priority: { include: [], exclude: [] },
        assignees: { include: [], exclude: [] },
        labels: { include: [], exclude: [] },
        dueDate: { include: [], exclude: [] }
    });

    const originalTitleRef = useRef(document.title);
    const boardRef = useRef(null);
    const scrollIntervalRef = useRef(null);
    const activeDragRef = useRef(false); // To track if a drag is active for global listeners

    // Function to handle switching cards within the modal
    const handleSwitchCardInModal = (newCardId) => {
        // Navigate to the new card's URL. The useEffect listening to cardId param will handle opening it.
        if (id && newCardId) {
            navigate(`/project/${id}/card/${newCardId}`);
        }
    };

    const SCROLL_AREA_WIDTH = 60; // px, increased sensitivity zone
    const SCROLL_SPEED = 10; // px per animation frame, adjusted speed

    useEffect(() => {
        const originalTitle = originalTitleRef.current;

        if (selectedCard && project) {
            document.title = `${selectedCard.title} | ${project.title}`;
        } else if (project) {
            document.title = project.title;
        } else {
            document.title = 'Project';
        }

        return () => {
            document.title = originalTitle;
        };
    }, [selectedCard, project]);

    useEffect(() => {
        if (cardId && project && !loading) {
            let foundCard = null;
            let foundColumn = null;
            for (const columnId in project.columns) {
                const column = project.columns[columnId];
                const card = column.cards.find(c => c.id === cardId);
                if (card) {
                    foundCard = card;
                    foundColumn = column;
                    break;
                }
            }

            if (foundCard && foundColumn) {
                setSelectedCard(foundCard);
                setSelectedColumn(foundColumn);
            } else {
                // Optional: Navigate back to project page if cardId is invalid or not found
                // navigate(`/project/${id}`);
            }
        }
    }, [cardId, project, loading, navigate, id]);

    useEffect(() => {
        if (project && typeof project.limitColumnHeight !== 'undefined') {
            setLimitColumnHeight(project.limitColumnHeight);
        }
        // We only want this effect to run when the project object itself is fetched or changes,
        // or when setLimitColumnHeight changes (which it shouldn't).
        // Adding project.id ensures it runs if the user navigates to a different project.
    }, [project?.id, project?.limitColumnHeight, setLimitColumnHeight]);
    // project.limitColumnHeight is added to dependencies to ensure that if it changes in DB
    // and projectData hook refetches, we update the local state.

    useEffect(() => {
        if (project) {
            if (project.settings && typeof project.settings.enableGithubLinks !== 'undefined') {
                setEnableGithubLinks(project.settings.enableGithubLinks);
            } else if (typeof project.enableGithubLinks !== 'undefined') {
                // Fallback for older structure if settings map isn't used yet.
                // Consider migrating if this path is taken.
                setEnableGithubLinks(project.enableGithubLinks);
            }
            // If not defined in project, it defaults to useLocalStorage value (true in this case)
        }
    }, [project?.id, project?.settings?.enableGithubLinks, project?.enableGithubLinks, setEnableGithubLinks]);

    useEffect(() => {
        if (!accessLoading && !hasAccess) {
            navigate('/');
        }
    }, [accessLoading, hasAccess, navigate]);

    useEffect(() => {
        const handleResize = () => {
            const textarea = document.querySelector('textarea');
            if (textarea) {
                handleTextAreaHeight({ target: textarea });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const textarea = document.querySelector('textarea');
        if (textarea && !isCollapsed) {
            handleTextAreaHeight({ target: textarea });
        }
    }, [project?.description, isCollapsed]);

    const updateLocalProject = (updates) => {
        setProject(prevProject => ({
            ...prevProject,
            ...updates,
            updatedAt: new Date().toISOString()
        }));
    };

    const handleTextAreaHeight = (e) => {
        const element = e.target;
        const maxHeight = window.innerHeight - 300;
        element.style.height = 'auto';
        const newHeight = Math.min(element.scrollHeight, maxHeight);
        element.style.height = `${newHeight}px`;
        setTextareaHeight(`${newHeight}px`);
    };

    const handleToggleLimitColumnHeight = async () => {
        console.log('Click here');
        try {
            await updateProject(id, {
                limitColumnHeight: !limitColumnHeight
            });
            setLimitColumnHeight(!limitColumnHeight);
        } catch (error) {
            console.error('Error updating column height setting:', error);
        }
    };

    const handleToggleEnableGithubLinks = async () => {
        const newValue = !enableGithubLinks;
        try {
            await updateProject(id, { 'settings.enableGithubLinks': newValue });
            setEnableGithubLinks(newValue);
        } catch (error) {
            console.error('Error updating GitHub links setting:', error);
            // Optionally revert state or notify user
        }
    };

    const handleBackgroundChange = async (newBackground) => {
        try {
            await updateProject(id, {
                background: newBackground
            });
            // Update local state to reflect the change immediately
            setProject(prevProject => ({
                ...prevProject,
                background: newBackground
            }));
        } catch (error) {
            console.error('Error updating background:', error);
        }
    };

    const handleArchiveAllCards = async (columnId) => {
        try {
            const column = project.columns[columnId];
            const updatedCards = column.cards.map(card => ({
                ...card,
                archived: true,
                archivedAt: new Date().toISOString()
            }));

            const activityEntry = {
                id: `activity-${Date.now()}`,
                action: `archived all cards in list "${column.title}"`,
                timestamp: new Date().toISOString(),
                userId: user.uid,
                userName: user.displayName || user.email,
            };

            await updateProject(id, {
                [`columns.${columnId}.cards`]: updatedCards,
                activities: [activityEntry, ...(project.activities || [])],
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error archiving all cards:', error);
        }
    };

    const getArchivedCards = () => {
        const allCards = [];
        Object.values(project.columns).forEach(column => {
            column.cards.forEach(card => {
                if (card.archived) {
                    allCards.push({ ...card, columnId: column.id });
                }
            });
        });
        return allCards;
    };

    const onDragEnd = async (result) => {
        const { destination, source, draggableId, type } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        if (type === 'CHECKLIST_ITEM') {
            await handleTodoOperations.reorder(id, project, result);
            return;
        }

        if (type === 'COLUMN') {
            const newColumnOrder = Array.from(project.columnOrder);
            newColumnOrder.splice(source.index, 1);
            newColumnOrder.splice(destination.index, 0, draggableId);

            updateLocalProject({ columnOrder: newColumnOrder });
            await handleColumnOperations.reorder(id, project, newColumnOrder);
            return;
        }

        // Card drag and drop logic
        const startColumnFull = project.columns[source.droppableId];
        const endColumnFull = project.columns[destination.droppableId];

        if (!startColumnFull || !endColumnFull) {
            console.error('Column not found:', { source: source.droppableId, destination: destination.droppableId });
            return;
        }

        const movedCard = startColumnFull.cards.find(card => card.id === draggableId);
        if (!movedCard) {
            console.error('Card not found:', draggableId);
            return;
        }

        // Get the filtered cards as they appear in the UI for index reference
        // The `filteredColumns` variable is already available in the component's scope
        const startColumnFilteredCards = filteredColumns[source.droppableId]?.cards || [];
        const endColumnFilteredCards = filteredColumns[destination.droppableId]?.cards || [];

        let newStartCardsFull = Array.from(startColumnFull.cards);
        let newEndCardsFull;

        if (source.droppableId === destination.droppableId) {
            // Reordering in the same column
            const visibleCardIdsOrder = startColumnFilteredCards.map(c => c.id);
            const [removedCardId] = visibleCardIdsOrder.splice(source.index, 1);
            visibleCardIdsOrder.splice(destination.index, 0, removedCardId);

            // Create a map of visible cards for quick lookup
            const visibleCardsMap = new Map(startColumnFilteredCards.map(c => [c.id, c]));

            let finalCards = [];
            let visibleIdx = 0;
            for (const card of newStartCardsFull) {
                if (visibleCardsMap.has(card.id)) {
                    // This card is visible, place it according to the new order
                    // but only add it when its turn comes in `visibleCardIdsOrder`
                    // This check ensures that we add hidden cards that might be before the first visible card
                } else {
                    // This card is hidden, keep it
                    finalCards.push(card);
                }
            }

            // Now, merge the reordered visible cards with the hidden cards
            let hiddenCardPointer = 0;
            let reorderedFinalCards = [];
            for(const id of visibleCardIdsOrder) {
                const cardToInsert = visibleCardsMap.get(id);
                // Find the original index of this card in the full list to know how many hidden cards were before it.
                // This is tricky because the original relative order of hidden cards needs to be maintained.

                // A simpler way: build the list by taking cards from either the new visible order or the preserved hidden cards.
            }

            // Simplified approach for same column reorder:
            // 1. Remove the card from its original position in the full list.
            newStartCardsFull = newStartCardsFull.filter(card => card.id !== draggableId);

            // 2. Determine the correct insertion index in the full list.
            //    If destination.index is 0, insert at the beginning of where visible cards start.
            //    If destination.index is N, insert after the Nth visible card in the full list.
            let targetIndexInFullList;
            if (destination.index === 0) {
                // Find the index of the first visible card in the original full list
                const firstVisibleCardInFullListId = endColumnFilteredCards[0]?.id;
                if (firstVisibleCardInFullListId) {
                    targetIndexInFullList = newStartCardsFull.findIndex(c => c.id === firstVisibleCardInFullListId);
                    if (targetIndexInFullList === -1) { // Should not happen if logic is correct
                         // Fallback: if the target visible card isn't found (e.g., list was all hidden cards, then one added)
                        // Or if all cards are visible, this logic is simpler.
                        // Let's use the destination.index relative to the filtered list to find the anchor card in the full list.
                        const anchorCardId = endColumnFilteredCards[destination.index]?.id;
                        if (anchorCardId) {
                            targetIndexInFullList = newStartCardsFull.findIndex(c => c.id === anchorCardId);
                        } else { // Dropping at the end of visible cards
                            if (endColumnFilteredCards.length > 0) {
                                const lastVisibleCardId = endColumnFilteredCards[endColumnFilteredCards.length -1].id;
                                const idx = newStartCardsFull.findIndex(c => c.id === lastVisibleCardId);
                                targetIndexInFullList = idx + 1;

                            } else { // No visible cards in destination (empty or all hidden)
                                targetIndexInFullList = newStartCardsFull.length; // Add to the end of full list
                            }
                        }
                    }
                } else { // No visible cards in destination (empty or all hidden), dropping at the start
                    targetIndexInFullList = 0; // Add to the start of full list
                }
            } else {
                 // Find the card in the full list that was *before* the drop target in the filtered list
                const anchorCardId = endColumnFilteredCards[destination.index - 1]?.id;
                const anchorIndexInFullList = newStartCardsFull.findIndex(c => c.id === anchorCardId);

                if (anchorIndexInFullList !== -1) {
                    targetIndexInFullList = anchorIndexInFullList + 1;
                } else {
                    // Fallback if anchor is not found (e.g. first item moved to last)
                    // This case needs to be robust: if the anchor was the moved card itself (now removed)
                    // or if dropping at the very end of a list that had hidden items at the end.
                    // The most reliable is to count visible items up to destination.index
                    let visibleCount = 0;
                    let insertionPoint = 0;
                    for(const card of newStartCardsFull) {
                        if(filteredColumns[source.droppableId].cards.find(fc => fc.id === card.id)) { // Check if card is in filtered list
                            if(visibleCount === destination.index) break;
                            visibleCount++;
                        }
                        insertionPoint++;
                    }
                    targetIndexInFullList = insertionPoint;
                }
            }

            newStartCardsFull.splice(targetIndexInFullList, 0, movedCard);
            newEndCardsFull = newStartCardsFull; // For same column, start and end are the same

            const updatedColumns = {
                ...project.columns,
                [startColumnFull.id]: {
                    ...startColumnFull,
                    cards: newStartCardsFull
                }
            };
            updateLocalProject({ columns: updatedColumns });
            await handleCardOperations.move(
                id,
                project, // Pass the original project state for backend consistency
                startColumnFull,
                endColumnFull, // Same as startColumnFull
                movedCard,
                newStartCardsFull, // Send the updated full list
                newEndCardsFull  // Same as newStartCardsFull
            );

        } else {
            // Moving to a different column
            // 1. Remove card from source column's full list
            newStartCardsFull = newStartCardsFull.filter(card => card.id !== draggableId);

            // 2. Determine insertion index in destination column's full list
            let newDestCardsFull = Array.from(endColumnFull.cards);
            let targetIndexInFullList;

            if (destination.index === 0) {
                // If dropping at the beginning of visible cards in dest column
                const firstVisibleCardInDestFullListId = endColumnFilteredCards[0]?.id;
                if (firstVisibleCardInDestFullListId) {
                    targetIndexInFullList = newDestCardsFull.findIndex(c => c.id === firstVisibleCardInDestFullListId);
                     if (targetIndexInFullList === -1) targetIndexInFullList = 0; // Fallback
                } else { // No visible cards in destination, or list is empty
                    targetIndexInFullList = 0;
                }
            } else {
                // Find the card in the dest full list that was *before* the drop target in the filtered list
                const anchorCardId = endColumnFilteredCards[destination.index - 1]?.id;
                const anchorIndexInFullList = newDestCardsFull.findIndex(c => c.id === anchorCardId);

                if (anchorIndexInFullList !== -1) {
                    targetIndexInFullList = anchorIndexInFullList + 1;
                } else {
                    // Fallback: Count visible items in dest column up to destination.index
                    let visibleCount = 0;
                    let insertionPoint = 0;
                    for(const card of newDestCardsFull) {
                        // Check if card is in the filtered list of the destination column
                        if(filteredColumns[destination.droppableId].cards.find(fc => fc.id === card.id)) {
                            if(visibleCount === destination.index) break;
                            visibleCount++;
                        }
                        insertionPoint++;
                    }
                    targetIndexInFullList = insertionPoint;
                }
            }

            // Add activity log for status change
            const activity = addActivity(user, {
                type: 'status_change',
                from: startColumnFull.title,
                to: endColumnFull.title,
            });

            const cardWithActivity = { ...movedCard, activity: [...(movedCard.activity || []), activity] };

            // New logic to handle due date completion
            if (endColumnFull.title === 'Closed' && cardWithActivity.dueDate) {
                cardWithActivity.isDueDateCompleted = true;
            } else if (startColumnFull.title === 'Closed' && cardWithActivity.dueDate) {
                cardWithActivity.isDueDateCompleted = false;
            }

            newDestCardsFull.splice(targetIndexInFullList, 0, cardWithActivity); // Add card with activity
            newEndCardsFull = newDestCardsFull;

            const updatedColumns = {
                ...project.columns,
                [startColumnFull.id]: {
                    ...startColumnFull,
                    cards: newStartCardsFull
                },
                [endColumnFull.id]: {
                    ...endColumnFull,
                    cards: newEndCardsFull
                }
            };
            updateLocalProject({ columns: updatedColumns });
            await handleCardOperations.move(
                id,
                project, // Original project state
                startColumnFull,
                endColumnFull,
                movedCard,
                newStartCardsFull, // Updated full list for source
                newEndCardsFull   // Updated full list for destination
            );
        }
    };

    const startScrolling = (direction) => {
        if (!boardRef.current) return;
        // If already scrolling in the same direction, let it continue
        if (scrollIntervalRef.current && scrollIntervalRef.current.direction === direction) return;

        // If scrolling in a different direction, stop current and start new
        if (scrollIntervalRef.current && scrollIntervalRef.current.direction !== direction) {
            stopScrolling();
        }

        scrollIntervalRef.current = { direction: direction, id: null };

        const scrollStep = () => {
            if (boardRef.current && scrollIntervalRef.current && scrollIntervalRef.current.direction === direction) {
                boardRef.current.scrollLeft += direction * SCROLL_SPEED;
                scrollIntervalRef.current.id = requestAnimationFrame(scrollStep);
            } else {
                // This ensures that if direction changes or scrolling is stopped externally, the loop terminates.
                stopScrolling();
            }
        };
        scrollIntervalRef.current.id = requestAnimationFrame(scrollStep);
    };

    const stopScrolling = () => {
        if (scrollIntervalRef.current && scrollIntervalRef.current.id) {
            cancelAnimationFrame(scrollIntervalRef.current.id);
        }
        scrollIntervalRef.current = null;
    };

    const handlePointerMoveDuringDrag = (event) => {
        if (!activeDragRef.current || !boardRef.current) {
            stopScrolling();
            return;
        }

        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const boardRect = boardRef.current.getBoundingClientRect();

        if (clientX < boardRect.left + SCROLL_AREA_WIDTH) {
            startScrolling(-1); // Scroll left
        } else if (clientX > boardRect.right - SCROLL_AREA_WIDTH) {
            startScrolling(1); // Scroll right
        } else {
            stopScrolling();
        }
    };

    const handleDragStart = () => {
        activeDragRef.current = true;
        window.addEventListener('mousemove', handlePointerMoveDuringDrag);
        window.addEventListener('touchmove', handlePointerMoveDuringDrag, { passive: false }); // passive:false if preventDefault might be needed
    };

    const onDragEndInternal = async (result) => {
        activeDragRef.current = false;
        window.removeEventListener('mousemove', handlePointerMoveDuringDrag);
        window.removeEventListener('touchmove', handlePointerMoveDuringDrag);
        stopScrolling();
        await onDragEnd(result); // Call the original onDragEnd logic passed from props or defined above
    };

    // Cleanup global listeners and scroll interval on component unmount
    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handlePointerMoveDuringDrag);
            window.removeEventListener('touchmove', handlePointerMoveDuringDrag);
            stopScrolling();
            activeDragRef.current = false;
        };
    }, []); // Empty dependency array means this runs on unmount only

    if (loading || accessLoading) return <Loader text="Loading project..." />;
    if (error) return <div>{error}</div>;
    if (!project) return <div>Project not found</div>;
    if (!hasAccess) return <div>You don't have access to this project.</div>;

    const filteredTodos = (project.todos || []).filter(todo =>
        todo.text.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredColumns = Object.keys(project.columns).reduce((acc, columnId) => {
        const column = project.columns[columnId];
        const filteredCards = column.cards.filter(card => {
            // Filter by search term
            const matchesSearchTerm = card.title.toLowerCase().includes(searchTerm.toLowerCase());
            if (!matchesSearchTerm) return false;

            // Helper function to check a single filter type (priority, assignees, labels, dueDate)
            const checkFilter = (filterName, cardValue, isArray = false, isNoneSelected = false) => {
                const filter = filters[filterName];
                if (!filter || (!filter.include?.length && !filter.exclude?.length)) {
                    return true; // No active filter for this type
                }

                // Check exclusions first
                if (filter.exclude && filter.exclude.length > 0) {
                    if (isArray) { // For labels (cardValue is an array of labelIds)
                        if (cardValue && cardValue.some(val => filter.exclude.includes(val))) {
                            return false; // Excluded if any of the card's labels are in the exclude list
                        }
                    } else if (filter.exclude.includes(cardValue) || (isNoneSelected && !cardValue && filter.exclude.includes('none'))) {
                         // For priority 'none' or direct match
                        return false; // Excluded
                    }
                }

                // Check inclusions if there are any
                if (filter.include && filter.include.length > 0) {
                    if (isArray) { // For labels
                        if (!cardValue || !cardValue.some(val => filter.include.includes(val))) {
                            return false; // Not included
                        }
                    } else if (isNoneSelected && filter.include.includes('none')) {
                        // If 'none' is in include, cardValue should be falsy OR match other includes
                        if (cardValue && !filter.include.includes(cardValue)) return false;
                        // If cardValue is present, it must be in the include list.
                        // If cardValue is not present, it matches 'none'.
                    }
                    else if (!filter.include.includes(cardValue)) {
                         if (!cardValue && filter.include.includes('none')) {
                            // This case is for when cardValue is undefined/null and 'none' is selected for inclusion.
                            // This logic is a bit complex because 'none' can be explicitly selected.
                            // If 'none' is included, and the card has no value, it passes.
                         } else if (cardValue || !filter.include.includes('none')) {
                            // If card has a value, it must be in include.
                            // If card has no value, and 'none' is not in include, it fails.
                           return false; // Not included
                         }
                    }
                }
                return true; // Passed all checks for this filter type
            };

            // Filter by priority
            if (!checkFilter('priority', card.priority, false, true)) return false;
            
            // Filter by assignee
            if (!checkFilter('assignees', card.assigneeId)) return false;
            
            // Filter by labels
            if (!checkFilter('labels', card.labelIds, true)) return false;

            // Due Date Filter Logic
            const dueDateFilter = filters.dueDate;
            if (dueDateFilter && (dueDateFilter.include?.length > 0 || dueDateFilter.exclude?.length > 0)) {
                const cardDueDate = card.dueDate ? dateFnsParseISO(card.dueDate) : null;
                let excludedByDueDate = false;
                let includedByDueDate = !dueDateFilter.include || dueDateFilter.include.length === 0; // True if no include filters

                // Check Due Date Exclusions
                if (dueDateFilter.exclude?.length > 0) {
                    if (dueDateFilter.exclude.some(filterId => {
                        switch (filterId) {
                            case 'no_due_date': return !cardDueDate;
                            case 'overdue': return cardDueDate && !dateFnsIsToday(cardDueDate) && dateFnsIsPast(cardDueDate);
                            case 'today': return cardDueDate && dateFnsIsToday(cardDueDate);
                            case 'this_week': return cardDueDate && isDateThisWeek(card.dueDate);
                            case 'next_week': return cardDueDate && isDateNextWeek(card.dueDate);
                            default: return false;
                        }
                    })) {
                        excludedByDueDate = true;
                    }
                }

                if (excludedByDueDate) return false;

                // Check Due Date Inclusions
                if (dueDateFilter.include?.length > 0) {
                    if (dueDateFilter.include.some(filterId => {
                        switch (filterId) {
                            case 'no_due_date': return !cardDueDate;
                            case 'overdue': return cardDueDate && !dateFnsIsToday(cardDueDate) && dateFnsIsPast(cardDueDate);
                            case 'today': return cardDueDate && dateFnsIsToday(cardDueDate);
                            case 'this_week': return cardDueDate && isDateThisWeek(card.dueDate);
                            case 'next_week': return cardDueDate && isDateNextWeek(card.dueDate);
                            default: return false;
                        }
                    })) {
                        includedByDueDate = true;
                    } else {
                        includedByDueDate = false; // Must match one include filter if any are active
                    }
                }

                if (!includedByDueDate) return false;
            }
            
            return true;
        });
        acc[columnId] = { ...column, cards: filteredCards };
        return acc;
    }, {});

    const filteredProject = {
        ...project,
        columns: filteredColumns,
    };

    return (
        <DragDropContext onDragStart={handleDragStart} onDragEnd={onDragEndInternal}>
            <ProjectContainer 
                background={
                    !background ? '#607D8B' : 
                    (background.type !== 'color' && background.urls?.full) ? background.urls.full : 
                    (background.color) ? background.color : 
                    '#607D8B'
                }
                isColor={!background || background.type === 'color'}
                fontColor={background?.fontColor || '#FFFFFF'}
                limitColumnHeight={limitColumnHeight}
            >
                <ContentContainer limitColumnHeight={limitColumnHeight}>
                    <ProjectHeader
                        project={project}
                        isCollapsed={isCollapsed}
                        setIsCollapsed={setIsCollapsed}
                        background={background}
                        onBackgroundChange={handleBackgroundChange}
                        archivedCards={getArchivedCards()}
                        onCardClick={(card) => {
                            setSelectedCard(card);
                            setSelectedColumn(project.columns[card.columnId]);
                            navigate(`/project/${id}/card/${card.id}`);
                        }}
                        textareaHeight={textareaHeight}
                        handleTextAreaHeight={handleTextAreaHeight}
                        limitColumnHeight={limitColumnHeight}
                        handleToggleLimitColumnHeight={handleToggleLimitColumnHeight}
                        enableGithubLinks={enableGithubLinks} // Pass new prop
                        handleToggleEnableGithubLinks={handleToggleEnableGithubLinks} // Pass new handler
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        filters={filters}
                        onFilterChange={setFilters}
                        labels={labels}
                        projectMembers={projectMembers}
                        onExportProject={() => exportProjectAsJson(project, {
                            columnOrder: project.columnOrder,
                            projectLabels: labels,
                            projectMembers,
                        })}
                    />

                    <TodoList
                        todos={filteredTodos}
                        onAddTodo={(text) => handleTodoOperations.add(id, project, text)}
                        onToggleTodo={(index) => handleTodoOperations.toggle(id, project, index)}
                        onDeleteTodo={(index) => handleTodoOperations.delete(id, project, index)}
                        onUpdateTodo={(index, text) => handleTodoOperations.update(id, project, index, text)}
                        onConvertToCard={(todo) => handleTodoOperations.convertToCard(id, project, todo, user)}
                        onConvertAllToCards={(todos) => handleTodoOperations.convertAllToCards(id, project, todos, user)}
                        isCollapsed={isTodoCollapsed}
                        onCollapse={setIsTodoCollapsed}
                        limitColumnHeight={limitColumnHeight}
                    />

                    <ProjectBoard
                        project={filteredProject}
                        labels={labels}
                        user={user}
                        enableGithubLinks={enableGithubLinks} // Pass down
                        areLabelsExpanded={areLabelsExpanded}
                        projectMembers={projectMembers}
                        onCardClick={(card, column) => {
                            setSelectedCard(card);
                            setSelectedColumn(column);
                            navigate(`/project/${id}/card/${card.id}`);
                        }}
                        onArchiveAllCards={handleArchiveAllCards}
                        onToggleLabels={() => setAreLabelsExpanded(!areLabelsExpanded)}
                        limitColumnHeight={limitColumnHeight}
                        collapsedColumnIds={collapsedColumnIds}
                        onColumnCollapseToggle={(columnId) => {
                            setCollapsedColumnIds(prev =>
                                prev.includes(columnId)
                                    ? prev.filter(id => id !== columnId)
                                    : [...prev, columnId]
                            );
                        }}
                        boardRef={boardRef} // <<< Add this prop
                    />

                    {selectedCard && selectedColumn && (
                        <CardModal
                            project={project}
                            card={selectedCard}
                            column={selectedColumn}
                            projectId={id}
                            projectMembers={projectMembers}
                            allColumns={project.columns}
                            columnOrder={project.columnOrder}
                            onClose={() => {
                                setSelectedCard(null);
                                setSelectedColumn(null);
                                navigate(`/project/${id}`);
                            }}
                            enableGithubLinks={enableGithubLinks} // Pass down the setting
                            onSwitchCard={handleSwitchCardInModal} // Pass the handler for direct navigation
                        />
                    )}
                </ContentContainer>

                {background?.type === 'image' && background?.credit && (
                    <BackgroundCredit>
                        Photo by{' '}
                        <a href={background.credit.link} target="_blank" rel="noopener noreferrer">
                            {background.credit.name}
                        </a>
                        {' '}on Unsplash
                    </BackgroundCredit>
                )}

                <SwitchBoardButton onClick={() => setIsSwitchBoardOpen(true)} />
                <SwitchBoardModal
                    isOpen={isSwitchBoardOpen}
                    onClose={() => setIsSwitchBoardOpen(false)}
                    currentProjectId={id}
                />
            </ProjectContainer>
        </DragDropContext>
    );
}

export default ProjectPage;
